import { useParams, useNavigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Settings, Trash2, Copy, Shield, AlertTriangle, ChevronRight } from 'lucide-react';
import { useBrands } from '@/lib/BrandsProvider';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';

// Import tab components
import GlobalSettingsTab from './tabs/GlobalSettingsTab';

export const BrandSettings: React.FC = () => {
  const { id: brandId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedBrand, setSelectedBrand, refreshBrands } = useBrands();
  const [localBrand, setLocalBrand] = useState(selectedBrand);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  // Update local brand when selectedBrand changes
  useEffect(() => {
    setLocalBrand(selectedBrand);
  }, [selectedBrand]);

  // Function to handle brand updates from child components
  const handleBrandUpdate = (updatedBrand: any) => {
    setLocalBrand(updatedBrand);
    setSelectedBrand(updatedBrand);
  };

  // Copy brand ID to clipboard
  const handleCopyBrandId = () => {
    if (brandId) {
      navigator.clipboard.writeText(brandId).then(() => {
        toast({
          title: "Brand ID Copied",
          description: `Brand ID ${brandId} has been copied to clipboard`,
        });
      }).catch(() => {
        toast({
          title: "Copy Failed",
          description: "Failed to copy brand ID to clipboard",
          variant: "destructive",
        });
      });
    }
  };

  // Delete brand (soft delete - mark as DELETED)
  const handleDeleteBrand = async () => {
    if (!brandId || !selectedBrand) return;
    
    const brandName = selectedBrand.brand_name;
    
    // Check if user entered correct brand name
    if (confirmationText !== brandName) {
      toast({
        title: "Invalid Confirmation",
        description: "The brand name doesn't match. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);

    try {
      // Soft delete - mark brand as DELETED
      const { error } = await supabase
        .from('brands')
        .update({ 
          status: 'DELETED',
          updated_at: new Date().toISOString()
        })
        .eq('id', brandId);

      if (error) throw error;

      toast({
        title: "Brand Deleted",
        description: `"${brandName}" has been deleted successfully`,
      });

      // Clear selected brand and refresh brands list
      setSelectedBrand(null);
      await refreshBrands();

      // Navigate to brands list
      navigate('/brands');
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete brand. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setConfirmationText('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      <div className="relative container mx-auto p-6 max-w-7xl space-y-8">
        
        {/* Enhanced Header Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/40 rounded-2xl border border-blue-200/50 shadow-sm p-8">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full -translate-y-20 translate-x-20" />
          <div className="relative">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg">
                  <Settings className="h-8 w-8 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent mb-2">
                    {localBrand?.brand_name || selectedBrand?.brand_name || 'Brand'} Settings
                  </h1>
                  <p className="text-gray-600 text-lg leading-relaxed">
                    Configure global settings and manage your brand
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/brands/${brandId}`)}
                  className="bg-white/50 border-white/50 hover:bg-white/70 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 mr-2" />
                  Back to Brand
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Management Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50/80 via-slate-50/60 to-white rounded-2xl border border-gray-200/60 shadow-sm">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50/30 via-transparent to-slate-50/20" />
          <div className="relative backdrop-blur-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-gray-100 to-slate-200 rounded-xl shadow-sm">
                <Shield className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Brand Management</h3>
                <p className="text-sm text-gray-600">Administrative actions for your brand</p>
              </div>
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              {/* Copy Brand ID Card */}
              <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50/20 via-transparent to-indigo-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Copy className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">Brand ID</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Copy your brand ID for support or integration purposes
                      </p>
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-700">
                        {brandId || 'N/A'}
                      </code>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyBrandId}
                      className="w-full bg-white hover:bg-blue-50 border-blue-200 text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      <Copy className="h-3 w-3 mr-2" />
                      Copy Brand ID
                    </Button>
                  </div>
                </div>
              </div>

              {/* Delete Brand Card */}
              <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-r from-red-50/20 via-transparent to-pink-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-4">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 mb-1">Delete Brand</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Permanently remove this brand and all associated data
                      </p>
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                        <span className="text-xs text-amber-700">This action cannot be undone</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={isDeleting}
                      className="w-full bg-white hover:bg-red-50 border-red-200 text-red-600 hover:text-red-700 transition-colors"
                    >
                      <Trash2 className="h-3 w-3 mr-2" />
                      {isDeleting ? 'Deleting...' : 'Delete Brand'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global Settings Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30 rounded-2xl border border-blue-200/50 shadow-sm">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" />
          <div className="relative">
            <GlobalSettingsTab 
              key={brandId}
              brandId={brandId}
              selectedBrand={localBrand || selectedBrand}
              setSelectedBrand={handleBrandUpdate}
            />
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-red-600">Delete Brand</DialogTitle>
              <DialogDescription className="text-gray-600">
                This action will permanently delete <span className="font-semibold">"{selectedBrand?.brand_name}"</span> and all associated data including:
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li>All content and campaigns</li>
                    <li>Platform connections and settings</li>
                    <li>Analytics and history</li>
                    <li>All convergences and trends</li>
                  </ul>
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Type <span className="font-semibold">"{selectedBrand?.brand_name}"</span> to confirm deletion:
                </label>
                <Input
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Enter brand name"
                  className="w-full"
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteDialogOpen(false);
                  setConfirmationText('');
                }}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteBrand}
                disabled={isDeleting || confirmationText !== selectedBrand?.brand_name}
              >
                {isDeleting ? 'Deleting...' : 'Delete Brand'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      <Toaster />
    </div>
  );
};

export default BrandSettings;